import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

interface ToastProps {
  type: 'success' | 'error';
  title: string;
  message: string;
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({
  type,
  title,
  message,
  isVisible,
  onClose,
  duration = 4000
}) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setIsClosing(false);
    }
  }, [isVisible]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  if (!isVisible) return null;

  const Icon = type === 'success' ? CheckCircle : AlertCircle;
  const bgColor = type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
  const iconColor = type === 'success' ? 'text-green-600' : 'text-red-600';
  const titleColor = type === 'success' ? 'text-green-800' : 'text-red-800';
  const progressColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-full">
      <div className={`${bgColor} border rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 ease-out ${
        isClosing ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
      }`}>
        <div className="p-4">
          <div className="flex items-start">
            <Icon className={`${iconColor} flex-shrink-0 mt-0.5 mr-3`} size={20} />
            <div className="flex-1 min-w-0">
              <h4 className={`text-sm font-semibold ${titleColor}`}>{title}</h4>
              <p className="text-sm text-gray-700 mt-1">{message}</p>
            </div>
            <button
              onClick={handleClose}
              className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        
        {/* CSS Progress Bar */}
        <div className="h-1 bg-gray-200">
          <div 
            className={`h-full ${progressColor} toast-progress`}
            style={{ 
              animationDuration: `${duration}ms`
            }}
            onAnimationEnd={() => {
              setIsClosing(true);
              setTimeout(onClose, 300);
            }}
          />
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{
        __html: `
          .toast-progress {
            width: 100%;
            animation: toast-shrink linear forwards;
          }
          @keyframes toast-shrink {
            from { width: 100%; }
            to { width: 0%; }
          }
        `
      }} />
    </div>
  );
};

export default Toast;